import React from 'react';

import { L10nDate } from '@travel/i18n';
import { Adult } from '@travel/icons/service';
import { Search } from '@travel/icons/ui';
import {
  JUST_WEEKDAY_PARENTHESES,
  MONTH_DAY_KANJI_DATE,
} from '@travel/traveler-core/utils/dateTime';
import { FlatButton } from '@travel/ui';
import { cx } from '@travel/utils/src/classNames';

import labels from 'favoriteList/components/SearchForm/constants/labels';

import styles from './searchEntryButton.module.scss';

export type Props = {
  wrapperClassName?: string;
  className?: string;
  /** Callback to be called when button is clicked */
  onClick?: () => void;
  /** Node to be inner content of button */
  children?: React.ReactNode;
  /** Boolean describing if Search Entry Button should be that of two rows */
  isTwoRowLayout?: boolean;
};

type MobileDateRowProps = {
  departureDate: string;
  returnDate: string;
  /** Node of departure date title */
  departureDateTitle?: React.ReactNode;
  /** Node of return date title */
  returnDateTitle?: React.ReactNode;
  /** Boolean describing if Search Entry Button should be that of two rows */
  isTwoRowLayout?: boolean;
  /*** Class Name */
  className?: string;
};

export const DateRow = (props: MobileDateRowProps) => {
  const {
    departureDate,
    returnDate,
    departureDateTitle = labels.checkIn,
    returnDateTitle = labels.checkOut,
    isTwoRowLayout = false,
    className,
  } = props;

  const produceDateBlock = (isDeparture: boolean, date?: string) => {
    return (
      <div className={styles.column}>
        <p
          className={cx(
            styles.row,
            isTwoRowLayout ? styles.smallTextWithBiggerLineHeight : styles.smallText,
            isTwoRowLayout && styles.twoRowLayout,
          )}
        >
          {isDeparture ? departureDateTitle : returnDateTitle}
        </p>
        <span
          className={cx(
            styles.row,
            styles.largeTextWithSmallerLineHeight,
            !isTwoRowLayout && styles.alignBaseline,
            isTwoRowLayout && styles.twoRowLayout,
          )}
        >
          {date ? (
            <>
              <L10nDate value={date} format={MONTH_DAY_KANJI_DATE} />
              <span className={styles.subText}>
                <L10nDate value={date} format={JUST_WEEKDAY_PARENTHESES} />
              </span>
            </>
          ) : (
            '--/--'
          )}
        </span>
      </div>
    );
  };

  return (
    <div className={cx(styles.rowInline, styles.floorFlow, className)}>
      {produceDateBlock(true, departureDate)}
      <span
        className={cx(
          isTwoRowLayout ? styles.sidePaddingSmall : styles.sidePadding,
          styles.smallText,
        )}
      >
        {'〜'}
      </span>
      {produceDateBlock(false, returnDate)}
    </div>
  );
};

type CapacityRowProps = {
  className?: string;
  adults?: number;
  children?: number;
  rooms?: number;
  isTwoRowLayout?: boolean;
  shouldHideIcon?: boolean;
};

export const CapacityRow = (props: CapacityRowProps) => {
  const { adults, children, rooms } = props;
  const { className, isTwoRowLayout = false, shouldHideIcon = false } = props;

  const twoRowLayout = (
    <div className={cx(styles.container, styles.horizontalUnitSpacing, className)}>
      <div className={styles.column}>
        <span data-testid="adult" className={styles.smallTextWithBiggerLineHeight}>
          {labels.adult}
        </span>
        <div className={styles.capacityTextWrapper}>
          <span className={styles.largeTextWithSmallerLineHeight}>{adults || '--'}</span>
          <span className={styles.smallTextWithBiggerLineHeight}>{labels.peopleCounterSP}</span>
        </div>
      </div>
      <div className={styles.column}>
        <span data-testid="child" className={styles.smallTextWithBiggerLineHeight}>
          {labels.children}
        </span>
        <div className={styles.capacityTextWrapper}>
          <span className={styles.largeTextWithSmallerLineHeight}>{children || '--'}</span>
          <span className={styles.smallTextWithBiggerLineHeight}>{labels.peopleCounterSP}</span>
        </div>
      </div>
      <div className={styles.column}>
        <span data-testid="room" className={styles.smallTextWithBiggerLineHeight}>
          {labels.numberOfRooms}
        </span>
        <div className={styles.capacityTextWrapper}>
          <span className={styles.largeTextWithSmallerLineHeight}>{rooms || '--'}</span>
          <span className={styles.smallTextWithBiggerLineHeight}>{labels.roomCounter}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className={cx(styles.rowInline, props.className)}>
      <span
        className={cx(styles.row, styles.zeroFlexShrink, isTwoRowLayout && styles.twoRowLayout)}
      >
        {shouldHideIcon ? null : (
          <Adult className={styles.iconSpacingRight} size={16} color="white" />
        )}
        {twoRowLayout}
      </span>
    </div>
  );
};

function SearchEntryButton(props: Props) {
  const { isTwoRowLayout = false } = props;
  return (
    <div className={props.wrapperClassName}>
      <FlatButton
        className={cx(styles.entryButton, props.className)}
        onClick={props.onClick}
        data-testid="searchEntryButton-main-button"
      >
        <span className={styles.container}>
          <Search className={styles.zeroFlexShrink} color="white" />
          <span className={cx(styles.paddingLeft, !isTwoRowLayout && styles.singleRowLayout)}>
            {props.children}
          </span>
        </span>
      </FlatButton>
    </div>
  );
}

export default SearchEntryButton;
